import { Form } from 'components/Wizards/Form/Form'
import { Formik, useFormikContext } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import Styles from './SectionsSelection.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useOutline } from 'components/Wizards/MultiStepBlogPostWizard/OutlineProvider'
import { useEffect, useState } from 'react'
import { NewsletterStyles } from 'components/Wizards/Newsletter/NewsletterStyleInput'
import { SectionValues } from './Types'
import { useUnsplashImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { useSections } from './SectionsProvider'
import { IntroductionField } from './IntroductionField'
import { Plus1 } from 'blaze-ui/icons/24'
import { useSubjectLineOptions } from '../SubjectLineSelection/SubjectLineOptionsProvider'
import { SectionsField } from './SectionsField'

export interface Values {
  selected_subject_line_id: string
  title: string
  audience?: string
  call_to_action?: string
  avoid?: string
  intro?: string
  source_material_ids?: number[]
  newsletter_style?: NewsletterStyles
  brand_voice_id?: number
  brand_kit_id?: number
  language?: string
}

export const SectionsSelection = multiStep<StepProps>((props) => {
  const { schema, initialValues, onSubmit } = props

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <Content {...props} />
    </Formik>
  )
})

const Content = (props: StepProps) => {
  const { schema, onBack, initialValues } = props
  const [photosAdded, setPhotosAdded] = useState(false)
  const [isGeneratingMore, setIsGeneratingMore] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const { abortGenerateIntro } = useOutline()
  const { isSubmitting } = useFormikContext()
  const {
    sections,
    setSections,
    addSection,
    generateSections,
    isLoading: isLoadingSections,
    abortGeneration,
  } = useSections()
  const { photos, isLoading: isLoadingImages, maxPhotos, setPage, page } = useUnsplashImages()
  const { subjectLineOptions } = useSubjectLineOptions()

  const selectedSubjectLine = subjectLineOptions.find((s) => s[0] === initialValues.selected_subject_line_id)?.[1]
  const isLoading = isLoadingImages || isLoadingSections

  const handleGenerateMore = () => {
    setIsGeneratingMore(true)
    setPhotosAdded(false)

    setPage(page + 1)
    generateSections({
      ...props.initialValues,
      title: selectedSubjectLine,
      section_titles: sections.map((s) => s.title),
    } as SectionValues)
  }

  const handleBack = () => {
    abortGenerateIntro()
    abortGeneration()
    onBack?.()
  }

  // When a new (or the first) batch of sections and photos are generated, assign the new photos to the new sections
  useEffect(() => {
    if (photosAdded || isLoading) return
    let indexOffset = 0

    setSections(
      sections.map((section) => {
        if (section.imageUrl !== undefined) return section

        const imageUrl = photos[maxPhotos * (page - 1) + indexOffset]?.urls.regular
        indexOffset++

        return {
          ...section,
          imageUrl,
        }
      }),
    )
    setPhotosAdded(true)
  }, [isLoading, maxPhotos, page, photos, photosAdded, sections, setSections])

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields className={Styles.formFields}>
            <Heading level={4} id="wizard-header">
              Confirm the intro and structure of your newsletter:
            </Heading>

            <IntroductionField {...props} />
            <SectionsField {...props} isGeneratingMore={isGeneratingMore} />

            <div className={Styles.actions}>
              <Button
                variant="tertiary"
                size={isMobile ? 'lg' : 'lg'}
                frontIcon={Edit1}
                onPress={addSection}
                isDisabled={isLoading}
              >
                Write My Own
              </Button>
              <Button
                variant="tertiary"
                size={isMobile ? 'lg' : 'lg'}
                frontIcon={Plus1}
                isDisabled={isLoading}
                onPress={handleGenerateMore}
              >
                Generate {maxPhotos} More
              </Button>
            </div>
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        <Form.PreviousButton isDisabled={isSubmitting} onPress={handleBack}>
          {isMobile ? '' : 'Back'}
        </Form.PreviousButton>
        <Form.PrimaryActions>
          <Form.SubmitButton isDisabled={isSubmitting}>{schema.describe().meta.submitLabel}</Form.SubmitButton>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
